// Import components
import { useEffect, useState } from "react";
import CreateButton from "global/components/CreateButton";
import { ImageLoader } from "global/components/ImageLoader";
import ToggleSwitch from "global/components/ToggleSwitch";
import TwoColumnList from "global/components/TwoColumnList";
import { CloseIcon } from "global/strich_icons/StrichIcons";
import LoadingScreen from "global/components/loading_ui/LoadingScreen";



// Render VIEW pop up - show larger display image with trim area opacity
const RenderViewPopup = ({ isOpen, onClose, view, handleUpdateView, handleDeleteView, deleteLoading }) => {

    // Store editable view form data
    const [viewData, setViewData] = useState({
        style: "",
        name: "",
        aspects: [],
        rotation: 0,
        active: "Y"
    });



    // Sync the form when the selected view changes
    useEffect(() => {

        // Return early if there is no view
        if (!view) { return; }

        // Read the supported aspects from the selected view
        let viewAspects = [];
        if (Array.isArray(view.aspects)) { viewAspects = [...view.aspects]; }

        // Set the form data from the selected view
        setViewData({
            style: view.style,
            name: view.name,
            aspects: viewAspects,
            rotation: view.rotation,
            active: view.active
        });

    }, [view]);



    // Build the view label from the view identity
    const viewLabel = view?.style + " / " + view?.name;



    // Handle text and select field changes
    const handleChange = (event) => {

        // Get the changed field name
        const fieldName = event.target.name;

        // Get the changed field value
        let fieldValue = event.target.value;

        // Convert rotation back to a number
        if (fieldName === "rotation") { fieldValue = Number(fieldValue); }

        // Update the form data
        setViewData((prev) => ({
            ...prev,
            [fieldName]: fieldValue
        }));

    };



    // Toggle one supported view aspect
    const handleAspectToggle = (aspect) => {

        // Update the current aspect selection
        setViewData((prev) => {

            // Check whether this aspect is already selected
            const aspectIsSelected = prev.aspects.includes(aspect);

            // Keep at least one supported aspect selected
            if (aspectIsSelected && prev.aspects.length === 1) { return prev; }

            // Start with the current aspect list
            let updatedAspects = [...prev.aspects];

            // Remove the selected aspect
            if (aspectIsSelected) {
                updatedAspects = updatedAspects.filter((selectedAspect) => selectedAspect !== aspect);
            } else {

                // Add the selected aspect
                updatedAspects.push(aspect);
            }

            // Return the updated form data
            return {
                ...prev,
                aspects: updatedAspects
            };

        });

    };



    // Render one selectable aspect button
    const renderAspectButton = (aspect, label) => {

        // Check whether this aspect is selected
        const isSelected = viewData.aspects.includes(aspect);

        // Build the button class
        let buttonClassName = "selectable__chip";
        if (isSelected) { buttonClassName += " selectable__chip--selected"; }

        // Render the option
        return (
            <button type="button" className={buttonClassName} aria-pressed={isSelected} onClick={() => handleAspectToggle(aspect)}>
                {label}
            </button>
        );

    };



    // Handle active toggle change
    const handleActiveToggle = (event) => {

        // Convert the toggle value to the API flag
        let active = "N";
        if (event.target.value) { active = "Y"; }

        // Build the updated view data so the immediate save does not use stale state
        const updatedViewData = {
            ...viewData,
            active: active
        };

        // Update the form data
        setViewData(updatedViewData);

        // Save the active change immediately
        handleUpdateView(view, updatedViewData);

    };



    // Handle save button click
    const handleSave = () => {

        // Save the view data
        handleUpdateView(view, viewData);

    };

    // Get the display asset - full size because we're showing a large pop up window with the big image
    const display = view?.assets.find((asset) => asset.type === "display") 

        // Fall back to the preview asset - should always exist (especially in the headers)
        || view?.assets.find((asset) => asset.type === "preview") 
        
        // Fall back to empty array
        || [];

    // Get the background image from the asset
    const backgroundImage = display?.layers?.find((layer) => layer.type === "background");

    // Get the trim image from the asset
    const trimImage = display?.layers?.find((layer) => layer.type === "trim");


    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;

    
    return (

        <div className="popup-overlay">
        
            <div className="popup-container popup-container--small" style={{height: "auto"}}>

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Edit View: {viewLabel}

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            Reference: {view?.reference}

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                         <ToggleSwitch
                            name="active"
                            value={viewData.active === "Y"}
                            labelPosition="right"
                            size="m"
                            onChange={handleActiveToggle}
                        />

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => onClose?.()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>

                <div className="popup-content popup-content__body">

                    <div className="view-popup_container">

                        <div className="view-popup_image-container" title={viewLabel}>
                            
                            <ImageLoader src={backgroundImage.image_url} alt="Preview" className="surface-details_image" wrapperCSS="NA" imageLoaderImageCSS="view_bg-image-opacity" />
                            <ImageLoader src={trimImage.image_url} alt="Trim" className="surface-details_image" wrapperCSS="NA" imageLoaderImageCSS="NA" />

                        </div>

                    </div>

                    <TwoColumnList
                        property={<label>Style</label>}
                        value={<input type="text" name="style" value={viewData.style} onChange={handleChange} />}
                    />

                    <TwoColumnList
                        property={<label>Name</label>}
                        value={<input type="text" name="name" value={viewData.name} onChange={handleChange} />}
                    />

                    <TwoColumnList
                        property={<label>Aspects</label>}
                        value={
                            <div className="selectable__chips">
                                {renderAspectButton("S", "Square")}
                                {renderAspectButton("T", "Tall")}
                                {renderAspectButton("W", "Wide")}
                            </div>
                        }
                    />

                    <TwoColumnList
                        property={<label>Rotation</label>}
                        value={
                            <select name="rotation" value={viewData.rotation} onChange={handleChange} disabled={view?.reference === "Y"}>
                                <option value="0">0</option>
                                <option value="90">90</option>
                                <option value="180">180</option>
                                <option value="270">270</option>
                            </select>
                        }
                    />

                </div>

                <br />

                <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                    <CreateButton title={deleteLoading ? "Deleting" : "Delete View"} isDelete={true} tooltip="Delete View" size="m" icon="/images/icon_bin.png" disabled={deleteLoading} onClick={() => handleDeleteView(view)} />

                    <CreateButton title="Save View" tooltip="Save View" size="m" onClick={handleSave} />

                </div>

            </div>

            {deleteLoading &&
            
                <LoadingScreen loadingMessage="Deleting..." /> 

            }

        </div>
    )

}; export default RenderViewPopup;